import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { first, map } from 'rxjs';
import { IDeactivate } from 'src/app/common/guards/save-data-guard/save-data.interface';
import ProjectLinks from 'src/app/common/project.links';
import { SendMessageFormService } from 'src/app/forms/conversation-forms/send-message-form.service';
import { UserInfo } from 'src/app/interfaces';
import { EntityStatus, ItemStatus } from 'src/app/interfaces/views.interface';
import { DialogActionTypes } from 'src/app/notifications/dialogs/dialogs.enum';
import { AccountStatus } from 'src/app/swagger-api';
import { promiseWithCatch, safelyDetectChanges } from '../../common/app.helpers';
import { ChatComponent } from './chat.component';
import {
  AccountStatusChat,
  getInterlocutorInternal,
  getPrivateConversationInterlocutorAccountStatus,
  isGroupConversation,
  isReadOnlyConversation,
} from './helpers/chat.helpers';
import { computeConversationEffectiveTitle } from './helpers/twilio-message.helpers';
import { ChatEngineChannelsService } from './interfaces/chat-engine/chat-engine-channels.service';
import { ChatEngineOperations } from './interfaces/chat-engine/chat-engine-error.service';
import { ChatChannelType, ChatConversation, ChatTraceData } from './interfaces/chat-model.interface';
import {
  ChatPermissions,
  ChatTitleSectionConfiguration,
  TitleUpdatedContent,
} from './interfaces/chat-operations.interface';
import { ChatBlockingService } from './services/chat-blocking.service';
import { ChatSendMessageToService } from './services/chat-send-message-to.service';
import { ChatTitleService } from './services/chat-title.service';

@Component({
  selector: 'app-chat',
  templateUrl: './conversations-chat.component.html',
  styleUrls: ['./chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationsChatComponent extends ChatComponent implements IDeactivate {
  public conversations: ChatConversation[];
  public activeConversationId = '';
  public titleConfiguration: ChatTitleSectionConfiguration | null = null;
  public isFormChanged = false;

  // Only for private conversations
  public privateConversationAccountStatus: EntityStatus = ItemStatus.EMPTY;

  public get displayAccountStatusHint(): boolean {
    // This should not involve non-private conversation
    if (this.privateConversationAccountStatus === ItemStatus.EMPTY) return false;
    return this.privateConversationAccountStatus !== AccountStatus.ACTIVE;
  }

  public get accountStatusHint(): string {
    if (this.privateConversationAccountStatus === AccountStatus.DEACTIVATED) {
      return AccountStatusChat.ACCOUNT_DEACTIVATED_TEXT;
    }
    if (this.privateConversationAccountStatus === AccountStatus.DELETED) {
      return AccountStatusChat.ACCOUNT_DELETED_TEXT;
    }
    return '';
  }

  private chatBlockingService = inject(ChatBlockingService);
  private chatTitleService = inject(ChatTitleService);
  private chatSendMessageToService = inject(ChatSendMessageToService);
  private chatEngineChannelsService = inject(ChatEngineChannelsService);
  private sendMessageFormService = inject(SendMessageFormService);

  public override ngOnInit(): void {
    super.ngOnInit();

    this.otherSubscriptions$.push(
      this.chatBlockingService.userBlocked.subscribe(() => {
        this.clearActiveConversation();
        this.chatEngineActiveChannelService.closeActiveChannel();
        promiseWithCatch(this.router.navigate([ProjectLinks.LINK_CHAT]));
      }),
    );

    this.subscribeToConversationTitleUpdated();
    this.subscribeToModalWindowChanges();
  }

  public initNewMessage(): void {
    // TODO: block sending when engine is not yet ready
    this.chatEngineOnSubscriptions$.push(
      this.chatSendMessageToService
        .sendMessageToNotYetSelectedUsers(false /* showConfirmation */)
        .subscribe((conversation: ChatConversation) => {
          this.onConversationSelected(conversation);
        }),
    );
  }

  public onConversationSelected(conversation: ChatConversation): void {
    promiseWithCatch(
      this.router.navigateByUrl(ProjectLinks.LINK_CHAT + '/' + conversation.relatedEntityId, { replaceUrl: true }),
    );
  }

  public blockUser(): void {
    if (this.activeConversation) {
      this.chatDialogsService.blockUserDialog(() => {
        const interlocutor: UserInfo | null = getInterlocutorInternal(
          this.activeConversation,
          this.sessionService.userId,
        );
        if (interlocutor) {
          this.chatBlockingService.blockUser(interlocutor);

          const activeConversationIndex: number = this.conversations.indexOf(this.activeConversation);
          if (activeConversationIndex !== -1) {
            this.conversations.splice(activeConversationIndex, 1);
          }
          this.onBackBtnClick();
        }
      });
    }
  }

  public override detectChanges(): void {
    safelyDetectChanges(this.changeDetector);
  }

  public onBackBtnClick(): void {
    this.clearActiveConversation();
    this.chatEngineActiveChannelService.closeActiveChannel();
    promiseWithCatch(this.router.navigate([ProjectLinks.LINK_CHAT]));
  }

  public onConversationTitleEditRequested(): void {
    this.chatDialogsService
      .showChangeConversationTitleEditDialogue(this.titleConfiguration.text)
      .pipe(first())
      .subscribe((response: string) => {
        if (!response) {
          return;
        } else if (response === DialogActionTypes.RESET_CONVERSATION_TITLE) {
          this.chatTitleService.resetConversationTitle(this.activeConversation, this.currentUserId);
        } else {
          this.chatTitleService.setConversationTitle(this.activeConversation, response);
        }
      });
  }

  public setFormStatus(dirty: boolean): void {
    this.isFormChanged = dirty;
    safelyDetectChanges(this.changeDetector);
  }

  public setDefaultValueForModal(): void {
    this.sendMessageFormService.resetFormStatus();
  }

  protected override onChatEngineReady(): void {
    super.onChatEngineReady();

    this.chatEngineOnSubscriptions$.push(
      this.chatEngineChannelsService.initialConversationsListLoaded
        .pipe(
          map((conversations: ChatConversation[]) =>
            conversations.filter(
              (conversation: ChatConversation) => conversation.channelType === ChatChannelType.CONVERSATION,
            ),
          ),
        )
        .subscribe((conversations: ChatConversation[]) => {
          this.onConversationsUpdated(conversations);

          this.subscribeToRouteChanges(this.route);
          this.subscribeToConversationsListUpdated();
        }),
    );

    this.chatEngineOnSubscriptions$.push(
      this.chatEngineActiveChannelService.activeChannelDetailsChanged.subscribe(() =>
        this.updateActiveConversationDetails(),
      ),
    );
  }

  protected override onChatEngineOff(): void {
    super.onChatEngineOff();
  }

  protected override onSessionChanged(): void {
    super.onSessionChanged();
    if (!this.sessionService.isSessionActive()) {
      this.clearActiveConversation();
      this.conversations = [];
    }
  }

  protected override onActiveChannelChanged(activeChannel: ChatConversation | null): void {
    super.onActiveChannelChanged(activeChannel);
    if (activeChannel) {
      this.updateActiveConversation(activeChannel);
    } else {
      this.clearActiveConversation();
    }
  }

  protected onConnectionStateChanged(): void {
    this.updateActiveConversationPermissions();
    if (this.chatEngineConnected && this.activeConversation) {
      this.openActiveConversation();
    }
  }

  private subscribeToConversationsListUpdated(): void {
    this.chatEngineOnSubscriptions$.push(
      this.chatEngineChannelsService.conversationsListChanged
        .pipe(
          map((conversations: ChatConversation[]) =>
            conversations.filter(
              (conversation: ChatConversation) => conversation.channelType === ChatChannelType.CONVERSATION,
            ),
          ),
        )
        .subscribe((conversations: ChatConversation[]) => {
          this.onConversationsUpdated(conversations);
        }),
    );
  }

  private onConversationsUpdated(conversations: ChatConversation[]): void {
    this.conversations = conversations;

    if (this.activeConversationId) {
      const conversation: ChatConversation | null = this.conversations.find(
        (c: ChatConversation) => c.relatedEntityId === this.activeConversationId,
      );
      if (conversation) {
        this.updateActiveConversation(conversation);
      } else {
        this.clearActiveConversation();
        this.chatEngineActiveChannelService.closeActiveChannel();
        promiseWithCatch(this.router.navigate([ProjectLinks.LINK_CHAT]));
      }
    }

    this.detectChanges();
  }

  private clearActiveConversation(): void {
    this.messages = [];
    this.channelExists = false;
    this.isLoadingMessages = false;
    this.activeConversationId = '';
    this.activeConversation = null;
    this.permissions = {} as ChatPermissions;
    this.titleConfiguration = null;
  }

  private chatResolver(params: Params): void {
    if (params['id'] && params['id'] !== this.activeConversationId) {
      this.clearActiveConversation();
      this.activeConversationId = params['id'] as string;
      this.openActiveConversation();
    }
  }

  private subscribeToRouteChanges(route: ActivatedRoute): void {
    this.chatEngineOnSubscriptions$.push(route.params.subscribe((params: Params) => this.chatResolver(params)));
  }

  private openActiveConversation(): void {
    const channelUniqueName: string = 'CONVERSATION-' + this.activeConversationId;
    this.chatEngineActiveChannelService.activateChannel(channelUniqueName).then(
      (success: boolean) => {
        if (!success) {
          this.chatDialogsService.showNotFoundConversationDialog();
        }
      },
      (error: unknown) => {
        this.chatEngineErrorService.onOperationFailed(ChatEngineOperations.OPEN_ACTIVE_CONVERSATION, error, {
          channelUniqueName,
        } as ChatTraceData);
      },
    );
  }

  private updateActiveConversation(conversation: ChatConversation): void {
    this.activeConversation = conversation;
    this.updateActiveConversationDetails();
  }

  private updateActiveConversationDetails(): void {
    if (this.activeConversation) {
      this.updateActiveConversationPermissions();

      this.titleConfiguration = {
        text: computeConversationEffectiveTitle(
          this.activeConversation.originalTitle,
          this.activeConversation.participatingUsers,
          this.sessionService.userId,
        ),
        showParticipantLinks: isGroupConversation(this.activeConversation),
      } as ChatTitleSectionConfiguration;
    }
  }

  private updateActiveConversationPermissions(): void {
    if (this.activeConversation) {
      const groupConversation: boolean = isGroupConversation(this.activeConversation);
      const readOnlyConversation: boolean = isReadOnlyConversation(this.activeConversation, this.sessionService.userId);
      if (!groupConversation) {
        const accountStatus = getPrivateConversationInterlocutorAccountStatus(
          this.activeConversation,
          this.sessionService.userId,
        );
        if (accountStatus) this.privateConversationAccountStatus = accountStatus;
      } else {
        this.privateConversationAccountStatus = null;
      }

      this.permissions = {
        canSendMessages: this.chatEngineConnected && !readOnlyConversation,
        canBlockChat: this.chatEngineConnected && !groupConversation && !readOnlyConversation,
      } as ChatPermissions;
    }
  }

  private subscribeToConversationTitleUpdated(): void {
    this.otherSubscriptions$.push(
      this.chatTitleService.conversationTitleUpdated.subscribe(({ id, newTitle }: TitleUpdatedContent) => {
        if (this.activeConversationId === id) {
          this.titleConfiguration = { ...this.titleConfiguration, text: newTitle };
        }
      }),
    );
  }

  private subscribeToModalWindowChanges(): void {
    this.otherSubscriptions$.push(
      this.sendMessageFormService.formChanged$.subscribe((changed: boolean) => {
        this.isFormChanged = changed;
      }),
    );
  }
}
